import * as THREE from "three";

export type ToothJaw = "upper" | "lower";

export type ToothAnchorHint = {
  /**
   * Normalized point inside the fitted realistic tooth bounds.
   * [0,0,0] = min corner
   * [1,1,1] = max corner
   */
  insertionPointRatio: [number, number, number];

  /**
   * Optional future-facing correction in degrees.
   */
  defaultRotationDeg?: [number, number, number];

  /**
   * Canonical "buccal/front" axis of the imported GLB in local space
   * after normalization.
   */
  buccalAxisLocal?: [number, number, number];

  /**
   * Canonical root / occlusal axis in local space.
   */
  rootAxisLocal?: [number, number, number];
};

function getToothNumber(toothId: string): number {
  const num = Number(toothId.replace("T", ""));
  if (!Number.isFinite(num)) {
    throw new Error(`Invalid toothId: ${toothId}`);
  }
  return num;
}

function getJaw(num: number): ToothJaw {
  return num <= 16 ? "upper" : "lower";
}

/**
 * Current numbering scheme in this project:
 * - Upper jaw: T1 ... T16 from LEFT to RIGHT
 * - Lower jaw: T17 ... T32 from LEFT to RIGHT
 *
 * Right side therefore is:
 * - Upper: T9 ... T16
 * - Lower: T25 ... T32
 */
function isRightSide(num: number): boolean {
  return (num >= 9 && num <= 16) || (num >= 25 && num <= 32);
}


export function getToothAnchorHint(toothId: string): ToothAnchorHint {
  const num = getToothNumber(toothId);
  const pos = getToothPosition(num);
  const isUpper = num <= 16;

  switch (pos) {
    case 1: // central incisor
    case 2: // lateral incisor
      return {
        insertionPointRatio: [0.5, 0.96, 0.5],
        defaultRotationDeg: [0, isUpper ? 90 : -90, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    case 3: // canine
      return {
        insertionPointRatio: [0.5, 0.94, 0.5],
        defaultRotationDeg: [0, isUpper ? 80 : -80, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    case 4: // first premolar
      return {
        insertionPointRatio: [0.5, 0.91, 0.5],
        defaultRotationDeg: [0, isUpper ? 70 : -70, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    case 5: // second premolar
      return {
        insertionPointRatio: [0.5, 0.91, 0.5],
        defaultRotationDeg: [0, isUpper ? 60 : -60, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    case 6: // first molar
      return {
        insertionPointRatio: [0.5, 0.88, 0.5],
        defaultRotationDeg: [0, isUpper ? 50 : -50, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    case 7: // second molar
      return {
        insertionPointRatio: [0.5, 0.88, 0.5],
        defaultRotationDeg: [0, isUpper ? 40 : -40, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    case 8: // third molar
      return {
        insertionPointRatio: [0.5, 0.88, 0.5],
        defaultRotationDeg: [0, isUpper ? 30 : -30, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };

    default:
      return {
        insertionPointRatio: [0.5, 0.9, 0.5],
        defaultRotationDeg: [0, isUpper ? 90 : -90, 0],
        buccalAxisLocal: [1, 0, 0],
        rootAxisLocal: [0, 1, 0],
      };
  }
}
/**
 * Kept for compatibility if other parts of the code still call it.
 * For the current display scheme, this returns a mirrored 1..8 family
 * position within the jaw sequence:
 *
 * Upper:
 *   T1  -> 8   (3rd molar)
 *   T8  -> 1   (central incisor)
 *   T9  -> 1   (central incisor)
 *   T16 -> 8   (3rd molar)
 *
 * Lower:
 *   T17 -> 8
 *   T24 -> 1
 *   T25 -> 1
 *   T32 -> 8
 */
function getToothPosition(num: number): number {
  if (num >= 1 && num <= 16) {
    const index = num; // 1..16
    return index <= 8 ? 9 - index : index - 8;
  }

  if (num >= 17 && num <= 32) {
    const index = num - 16; // 1..16
    return index <= 8 ? 9 - index : index - 8;
  }

  throw new Error(`Unsupported tooth number: ${num}`);
}

export function getToothMeshPath(toothId: string): string {
  const num = getToothNumber(toothId);

  const upperMap: Record<number, string> = {
    1: "/teeth/maxillary_third_molar.glb",
    2: "/teeth/maxillary_second_molar.glb",
    3: "/teeth/maxillary_first_molar.glb",
    4: "/teeth/maxillary_second_premolar.glb",
    5: "/teeth/maxillary_first_premolar.glb",
    6: "/teeth/maxillary_canine.glb",
    7: "/teeth/maxillary_lateral_incisor.glb",
    8: "/teeth/maxillary_left_central_incisor.glb",
    9: "/teeth/maxillary_left_central_incisor.glb",
    10: "/teeth/maxillary_lateral_incisor.glb",
    11: "/teeth/maxillary_canine.glb",
    12: "/teeth/maxillary_first_premolar.glb",
    13: "/teeth/maxillary_second_premolar.glb",
    14: "/teeth/maxillary_first_molar.glb",
    15: "/teeth/maxillary_second_molar.glb",
    16: "/teeth/maxillary_third_molar.glb",
  };

  const lowerMap: Record<number, string> = {
    17: "/teeth/mandibular_third_molar.glb",
    18: "/teeth/mandibular_second_molar.glb",
    19: "/teeth/mandibular_first_molar.glb",
    20: "/teeth/mandibular_left_second_premolar.glb",
    21: "/teeth/mandibular_first_premolar.glb",
    22: "/teeth/mandibular_left_canine.glb",
    23: "/teeth/mandibular_left_lateral_incisor.glb",
    24: "/teeth/mandibular_left_central_incisor.glb",
    25: "/teeth/mandibular_left_central_incisor.glb",
    26: "/teeth/mandibular_left_lateral_incisor.glb",
    27: "/teeth/mandibular_left_canine.glb",
    28: "/teeth/mandibular_first_premolar.glb",
    29: "/teeth/mandibular_left_second_premolar.glb",
    30: "/teeth/mandibular_first_molar.glb",
    31: "/teeth/mandibular_second_molar.glb",
    32: "/teeth/mandibular_third_molar.glb",
  };

  if (num >= 1 && num <= 16) {
    return upperMap[num] ?? "/teeth/maxillary_first_molar.glb";
  }

  if (num >= 17 && num <= 32) {
    return lowerMap[num] ?? "/teeth/mandibular_first_molar.glb";
  }

  throw new Error(`Unsupported toothId: ${toothId}`);
}

/**
 * Temporary catalog-level anchor hints.
 */


/**
 * SINGLE SOURCE OF TRUTH FOR RAW ASSET ORIENTATION
 */
export function applyRealisticToothTransform(
  object: THREE.Object3D,
  toothId: string
): void {
  const num = getToothNumber(toothId);

  if (num <= 16) {
    object.rotateX(Math.PI);
  }

  if (isRightSide(num)) {
    object.scale.x *= -1;
  }
}

export function getToothInfo(toothId: string) {
  const num = getToothNumber(toothId);

  return {
    number: num,
    position: getToothPosition(num),
    jaw: getJaw(num),
    isRightSide: isRightSide(num),
  };
}